import _get from 'lodash/get';
import _has from 'lodash/has';
import _isEmpty from 'lodash/isEmpty';
import _partition from 'lodash/partition';
import { getQuestionVisibility } from 'containers/Assessment/utils';
import { shouldDisplayQuestion } from 'components/DemographicsSelect/utils';

export const getClientDemographicsQuestions = clientDetails => {
  const questions = {};

  const config = _get(clientDetails, 'context.customDemographics');

  if (!_isEmpty(config)) {
    Object.keys(config).forEach(key => {
      if (Array.isArray(config[key].questionsNames)) {
        config[key].questionNames.forEach(questionName => {
          const { questionNames, options, ...props } = config[key];
          const parsedOptions = options.map(option => ({
            value: option.answerValue,
            text: option.label,
            readableValue: option.value,
          }));
          questions[questionName] = {
            ...props,
            choices: parsedOptions,
          };
        });
      }
    });
  }

  return questions;
};

const isEmptyOrLessThanZero = value => {
  if (typeof value === 'number') {
    return value < 0;
  }
  return _isEmpty(value);
};

export const getErrors = (pages, pageNo, answers, isMobile = false) => {
  const newError = [];
  const answersKeys = Object.keys(answers).filter(key =>
    Array.isArray(answers[key]) ? !_isEmpty(answers[key]) : _has(answers, key),
  );
  const elementsToInspect = [];
  pages[pageNo].elements.forEach(el => {
    if (el.type === 'panel') {
      const conditionallyVisible = getQuestionVisibility(el, answers);
      if (conditionallyVisible && !!el.elements)
        elementsToInspect.push(...el.elements);
    } else {
      elementsToInspect.push(el);
    }
  });
  elementsToInspect
    .filter(el => el.isRequired)
    .forEach(el => {
      const conditionallyVisible = getQuestionVisibility(el, answers);
      if (conditionallyVisible) {
        if (
          !answersKeys.includes(el.name) ||
          isEmptyOrLessThanZero(answers[el.name])
        ) {
          newError.push(el.name);
          if (el.type === 'matrix' || el.type === 'matrixdropdown') {
            el.rows.forEach(row => {
              newError.push(row.value);
            });
          }
        } else if (el.type === 'matrix' || el.type === 'matrixdropdown') {
          if (answers[el.name]) {
            const matrixAnswersKeys = Object.keys(answers[el.name]);

            const matrixQuestionsErrors = [];
            el.rows.forEach(row => {
              if (!matrixAnswersKeys.includes(row.value))
                matrixQuestionsErrors.push(row.value);
            });
            if (!_isEmpty(matrixQuestionsErrors)) {
              if (!isMobile) newError.push(el.name);
              newError.push(...matrixQuestionsErrors);
            }
          } else {
            if (!isMobile) newError.push(el.name);
            el.rows.forEach(row => {
              newError.push(row.value);
            });
          }
        } else if (el.type === 'text' && el.inputType === 'number') {
          const customProps = {};
          if (!_isEmpty(el.validators)) {
            const [numericValidator] = el.validators.filter(
              validator => validator.type === 'numeric',
            );
            if (_has(numericValidator, 'minValue')) {
              customProps.min = numericValidator.minValue;
            }
            if (_has(numericValidator, 'maxValue')) {
              customProps.max = numericValidator.maxValue;
            }
          }
          const parsedValue = Number(answers[el.name]);
          const maxLength = Math.max(
            _has(customProps, 'min') ? customProps.min.toString().length : 0,
            _has(customProps, 'max') ? customProps.max.toString().length : 0,
          );

          if (
            (_has(customProps, 'min') && parsedValue < customProps.min) ||
            (_has(customProps, 'max') && parsedValue > customProps.max) ||
            (maxLength >= 1 && answers[el.name].length > maxLength)
          )
            newError.push(el.name);
        }
      }
    });

  return newError;
};

export const parseCurrentState = (pages, pageNo, answers) => {
  let nextPage = 1;
  let hasQuestions = false;
  let isComplete = false;

  while (!hasQuestions) {
    nextPage += 1;
    if (pages.length < pageNo + nextPage) {
      hasQuestions = true;
      isComplete = true;
    }

    const page = pages[pageNo + nextPage - 1];
    const elements = [];
    _get(page, 'elements', []).forEach(el => {
      if (el.type === 'panel') {
        const conditionallyVisible = getQuestionVisibility(el, answers);
        if (conditionallyVisible && !!el.elements)
          elements.push(...el.elements);
      } else {
        elements.push(el);
      }
    });
    const filteredElements = elements.filter(el =>
      getQuestionVisibility(el, answers),
    );
    if (filteredElements.length > 0) hasQuestions = true;
  }

  let nextPageAfter = 0;
  let hasQuestionsAfter = false;
  while (!hasQuestionsAfter) {
    nextPageAfter += 1;
    if (pages.length < pageNo + nextPage + nextPageAfter) {
      hasQuestionsAfter = true;
      isComplete = true;
    }

    const page = pages[pageNo + nextPage + nextPageAfter - 1];
    const elements = [];
    _get(page, 'elements', []).forEach(el => {
      if (el.type === 'panel') {
        const conditionallyVisible = getQuestionVisibility(el, answers);
        if (conditionallyVisible && !!el.elements)
          elements.push(...el.elements);
      } else {
        elements.push(el);
      }
    });
    const filteredElements = elements.filter(el =>
      getQuestionVisibility(el, answers),
    );
    if (filteredElements.length > 0) hasQuestionsAfter = true;
  }

  return [nextPage, isComplete];
};

export const isPrevPageVisible = (pages, pageNo, answers) => {
  const page = pages[pageNo - 1];
  const elements = [];
  _get(page, 'elements', []).forEach(el => {
    if (el.type === 'panel') {
      const conditionallyVisible = getQuestionVisibility(el, answers);
      if (conditionallyVisible && !!el.elements) elements.push(...el.elements);
    } else {
      elements.push(el);
    }
  });
  const filteredElements = elements.filter(el =>
    getQuestionVisibility(el, answers),
  );
  return filteredElements.length > 0;
};

export const getCustomQuestions = ({
  referral,
  assessmentId,
  takenAssessments,
  siteConfig,
  clientShortName,
  profile,
}) => {
  const customQuestionConfig = _get(
    referral,
    'copy.customAssessmentQuestion',
    {},
  );
  const placement = _get(customQuestionConfig, 'placement');

  if (!_get(customQuestionConfig, 'show', false)) {
    return {
      data: [],
      placement,
      answered: [],
    };
  }

  const data = _get(customQuestionConfig, 'data', []).map(question => {
    if (
      question.choicesFromDemographics &&
      shouldDisplayQuestion(
        siteConfig,
        question.choicesFromDemographics,
        clientShortName,
      )
    ) {
      const parsedChoices = _get(
        siteConfig,
        ['config', question.choicesFromDemographics, 'default'],
        [],
      ).map(option => ({
        text: option.label,
        value: option.value,
      }));
      return {
        ...question,
        choices: parsedChoices,
      };
    }

    return question;
  });

  const takenAssessment = takenAssessments.find(
    a => a.assessment.assessmentID === assessmentId,
  );

  if (data && takenAssessment) {
    const { result } = takenAssessment;
    const [answered, unanswered] = _partition(
      data,
      question =>
        _has(result, question.name) ||
        (question.name === 'stateOfResidence' &&
          _has(profile, 'stateOfResidence')),
    );

    const [answeredToBeDisplayed, answeredToBeParsed] = _partition(
      answered,
      question => question.behavior === 'always',
    );
    const questionsToBeDisplayed = unanswered.concat(answeredToBeDisplayed);

    const parsedAnswered = answeredToBeParsed.map(question => ({
      ...question,
      answerValue: _get(result, question.name),
    }));

    return {
      data: questionsToBeDisplayed.sort(
        (a, b) => data.indexOf(a) - data.indexOf(b),
      ),
      placement,
      answered: parsedAnswered,
    };
  }

  return {
    data,
    placement,
    answered: [],
  };
};

export const getSurveyPages = (
  model,
  answers,
  customQuestions,
  clientDemographicsQuestions,
) => {
  const { data, placement } = customQuestions;
  const pages = _get(model, 'pages', []).filter(page =>
    getQuestionVisibility(page, answers, true),
  );

  let finalPages = pages;
  if (data.length) {
    if (!placement || placement === 'start') {
      const elements = data.concat(_get(pages, ['0', 'elements'], []));
      finalPages = [{ ...pages[0], elements }].concat(pages.slice(1));
    } else {
      const lastPage = pages[pages.length - 1];
      const elements = _get(lastPage, 'elements', []).concat(data);
      finalPages = [...pages.slice(0, -1), { ...lastPage, elements }];
    }
  }

  const clientDemographicsQuestionsNames = Object.keys(
    clientDemographicsQuestions,
  );
  return finalPages.map(page => {
    const parsedElements = _get(page, 'elements', []).map(element => {
      if (element.type === 'panel') {
        const innerElements = element.elements.map(innerElement => {
          if (!clientDemographicsQuestionsNames.includes(innerElement.name)) {
            return innerElement;
          }

          return {
            ...innerElement,
            ...clientDemographicsQuestions[innerElement.name],
          };
        });

        return {
          ...element,
          elements: innerElements,
        };
      }

      if (!clientDemographicsQuestionsNames.includes(element.name)) {
        return element;
      }

      return {
        ...element,
        ...clientDemographicsQuestions[element.name],
      };
    });

    return {
      ...page,
      elements: parsedElements,
    };
  });
};

export const getAssessmentQuestionReferral = (
  referrals,
  assessmentId,
  clientDetails,
) => {
  const referral = referrals.find(r => {
    if (r.type !== 'Assessment Questions') {
      return false;
    }

    if (!r.showCollection.items.some(a => a.sys.id === assessmentId)) {
      return false;
    }

    if (
      r.excludeClientCollection.items.some(
        c => c.shortName === clientDetails.shortName,
      )
    ) {
      return false;
    }

    if (
      r.clientCollection.items.length &&
      !r.clientCollection.items.some(
        c => c.shortName === clientDetails.shortName,
      )
    ) {
      return false;
    }

    return true;
  });

  return referral;
};
